import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { get, post, put } from "../api";
import { Time } from "../Time";
import { CategoryChip, CategorySelect, StagePill, StageSelect } from "../Classification";
import PlatformDomains from "./PlatformDomains";
import PlanEditor from "../revenue/PlanEditor";
import type {
  AuditRow,
  Category,
  ChannelDetail,
  CustomerDetail,
  MailboxDetail,
  Role,
  Stage,
  UserSummary,
} from "../api";

/// Shared plumbing for the admin tables: load, show a failure, reload after
/// a write. Each screen is a form and a table over the same three states, so
/// writing that out five times would be five chances to handle an error
/// differently.
function useResource<T>(path: string) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    void get<T>(path)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message));
  };

  useEffect(load, [path]);
  return { data, error, setError, reload: load };
}

function Err({ message }: { message: string | null }) {
  return message ? <div className="error">{message}</div> : null;
}

export default function Admin() {
  return (
    <div className="wrap wide">
      <h1>Admin</h1>
      <nav className="topbar" style={{ position: "static", height: "auto", padding: "10px 0", background: "transparent", borderBottom: "1px solid var(--line)" }}>
        <NavLink to="/admin" end className={({ isActive }) => (isActive ? "on" : "")}>Customers</NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => (isActive ? "on" : "")}>Users</NavLink>
        <NavLink to="/admin/domains" className={({ isActive }) => (isActive ? "on" : "")}>Platform domains</NavLink>
        <NavLink to="/admin/billing" className={({ isActive }) => (isActive ? "on" : "")}>Billing</NavLink>
        <NavLink to="/admin/ingestion" className={({ isActive }) => (isActive ? "on" : "")}>Ingestion</NavLink>
        <NavLink to="/admin/audit" className={({ isActive }) => (isActive ? "on" : "")}>Audit</NavLink>
      </nav>

      <Routes>
        <Route index element={<CustomersAdmin />} />
        <Route path="users" element={<UsersAdmin />} />
        <Route path="domains" element={<PlatformDomains />} />
        <Route path="billing" element={<DefaultBilling />} />
        <Route path="ingestion" element={<Ingestion />} />
        <Route path="audit" element={<Audit />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </div>
  );
}

// --- Billing ------------------------------------------------------------

/** The default plan: what a tenant mapped to nobody, or a customer with no
    contract entered, is estimated at. Customer plans are edited on the
    customer's Revenue tab. */
function DefaultBilling() {
  return (
    <>
      <h2>Default billing plan</h2>
      <p className="dim">
        Used to estimate tenants mapped to no customer, and customers before their own plan takes
        effect. Customer plans are edited on each customer's Revenue tab.
      </p>
      <PlanEditor />
    </>
  );
}

// --- Customers ----------------------------------------------------------

function CustomersAdmin() {
  const { data, error, setError, reload } =
    useResource<{ customers: CustomerDetail[] }>("/api/admin/customers?include_inactive=true");
  const [editing, setEditing] = useState<string | null>(null);
  const blank = {
    name: "",
    display_name: "",
    domains: "",
    category: null as Category | null,
    stage: null as Stage | null,
  };
  const [form, setForm] = useState(blank);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await post("/api/admin/customers", {
        name: form.name.trim(),
        display_name: form.display_name.trim(),
        domains: splitDomains(form.domains),
        category: form.category,
        stage: form.stage,
      });
      setForm(blank);
      reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <>
      <Err message={error} />

      <h2>Add a customer</h2>
      <form onSubmit={create}>
        <div className="row">
          <div>
            <label>Slug (permanent)</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="tvs_credit"
            />
          </div>
          <div>
            <label>Display name</label>
            <input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="TVS Credit"
            />
          </div>
          <div>
            <label>Domains (comma separated)</label>
            <input
              value={form.domains}
              onChange={(e) => setForm({ ...form, domains: e.target.value })}
              placeholder="tvscredit.com, tvscredit.in"
            />
          </div>
          <div className="tight">
            <label htmlFor="new-category">Category</label>
            <CategorySelect
              id="new-category"
              value={form.category}
              onChange={(category) => setForm({ ...form, category })}
            />
          </div>
          <div className="tight">
            <label htmlFor="new-stage">Stage</label>
            <StageSelect
              id="new-stage"
              value={form.stage}
              onChange={(stage) => setForm({ ...form, stage })}
            />
          </div>
          <div className="tight">
            <button className="primary" disabled={!form.name || !form.display_name}>
              Create
            </button>
          </div>
        </div>
        <p className="sub">
          The slug is what <code>make report C=…</code> and every saved link use, so it
          cannot be changed later. Our own domains are refused.
        </p>
      </form>

      <h2>Customers</h2>
      {!data ? (
        <div className="empty">Loading…</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Slug</th>
              <th>Display name</th>
              <th>Category</th>
              <th>Stage</th>
              <th>Domains</th>
              <th>Channels</th>
              <th>Platform</th>
              <th>Users</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.customers.map((c) =>
              editing === c.id ? (
                <CustomerEditRow
                  key={c.id}
                  customer={c}
                  onDone={() => {
                    setEditing(null);
                    reload();
                  }}
                  onCancel={() => setEditing(null)}
                  onError={setError}
                />
              ) : (
                <tr key={c.id} className={c.is_active ? "" : "off"}>
                  <td><code>{c.name}</code></td>
                  <td>{c.display_name}</td>
                  <td>{c.category ? <CategoryChip category={c.category} /> : "—"}</td>
                  <td><StagePill stage={c.stage} /></td>
                  <td className="mono">{c.domains.join(", ") || "—"}</td>
                  <td>{c.slack_channels}</td>
                  <td>
                    {/* Reads as a count and acts as a way in: the assignment
                        itself lives on the directory, which is where the
                        relation runs from. */}
                    <Link to={`/admin/domains?customer=${c.name}`}>{c.platform_domains}</Link>
                  </td>
                  <td>{c.assigned_users}</td>
                  <td>{c.is_active ? "yes" : "no"}</td>
                  <td><button onClick={() => setEditing(c.id)}>Edit</button></td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function CustomerEditRow({
  customer,
  onDone,
  onCancel,
  onError,
}: {
  customer: CustomerDetail;
  onDone: () => void;
  onCancel: () => void;
  onError: (m: string) => void;
}) {
  const [displayName, setDisplayName] = useState(customer.display_name);
  const [domains, setDomains] = useState(customer.domains.join(", "));
  const [active, setActive] = useState(customer.is_active);
  const [category, setCategory] = useState(customer.category);
  const [stage, setStage] = useState(customer.stage);

  const save = async () => {
    try {
      await put(`/api/admin/customers/${customer.name}`, {
        display_name: displayName,
        domains: splitDomains(domains),
        is_active: active,
        category,
        stage,
      });
      onDone();
    } catch (e) {
      onError((e as Error).message);
    }
  };

  return (
    <tr>
      <td><code>{customer.name}</code></td>
      <td><input value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></td>
      <td><CategorySelect value={category} onChange={setCategory} /></td>
      <td><StageSelect value={stage} onChange={setStage} /></td>
      <td><input value={domains} onChange={(e) => setDomains(e.target.value)} /></td>
      <td colSpan={3} />
      <td>
        <input
          type="checkbox"
          style={{ width: "auto" }}
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        <button className="primary" onClick={save}>Save</button>{" "}
        <button onClick={onCancel}>Cancel</button>
      </td>
    </tr>
  );
}

const splitDomains = (s: string) =>
  s.split(",").map((d) => d.trim()).filter(Boolean);

// --- Users --------------------------------------------------------------

/// The slugs a user is granted. The list endpoint sends them joined, and
/// `null` for an admin (who holds no grant rows) as well as for a CSM with
/// none -- the role, not this, says which.
const grantSlugs = (u: UserSummary): string[] =>
  u.role === "csm" && u.customers ? u.customers.split(", ") : [];

/// `null` is the panel closed; `"new"` is it open on a user not yet created.
type Editing = string | "new" | null;

function UsersAdmin() {
  const users = useResource<{ users: UserSummary[] }>("/api/admin/users?include_inactive=true");
  const customers = useResource<{ customers: CustomerDetail[] }>("/api/admin/customers");
  const [editing, setEditing] = useState<Editing>(null);
  const [showInactive, setShowInactive] = useState(false);

  const all = users.data?.users ?? [];
  const inactive = all.filter((u) => !u.is_active).length;
  // The user being edited stays listed even if hidden by the filter, so the
  // highlighted row never disappears from under the panel.
  const shown = all.filter((u) => showInactive || u.is_active || u.id === editing);
  const target = editing && editing !== "new" ? all.find((u) => u.id === editing) ?? null : null;

  return (
    <>
      <Err message={users.error ?? customers.error} />

      <div className="users-layout">
        <main className="users-main">
          <div className="users-head">
            <h2>Users</h2>
            <button type="button" onClick={() => setEditing("new")} disabled={editing === "new"}>
              Add user
            </button>
          </div>

          {!users.data ? (
            <div className="empty">Loading…</div>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Customers</th>
                    <th>Last sign-in</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      editing={editing === u.id}
                      onEdit={() => setEditing(u.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="dim">
            Admins see every customer, so they have nothing to assign.{" "}
            {showInactive ? "Inactive users are shown dimmed" : "Inactive users are hidden"} —{" "}
            <button type="button" className="linkish" onClick={() => setShowInactive(!showInactive)}>
              {showInactive ? "hide" : "show"} {inactive} inactive
            </button>
            . Nobody can sign in until they exist here, however valid their Google account.
          </p>
        </main>

        {editing && (editing === "new" || target) && (
          <UserPanel
            key={editing}
            user={target}
            users={all}
            customers={customers.data?.customers ?? []}
            onClose={() => setEditing(null)}
            onSaved={(id) => {
              setEditing(id);
              users.reload();
            }}
            onError={(m) => {
              users.setError(m);
              users.reload();
            }}
          />
        )}
      </div>
    </>
  );
}

function UserRow({
  user,
  editing,
  onEdit,
}: {
  user: UserSummary;
  editing: boolean;
  onEdit: () => void;
}) {
  const slugs = grantSlugs(user);
  return (
    <tr className={[user.is_active ? "" : "off", editing ? "editing" : ""].join(" ").trim()}>
      <td>
        <div>{user.name}</div>
        <div className="dim mono">{user.email}</div>
      </td>
      <td>{user.role}</td>
      <td>
        {user.role === "admin" ? (
          <span className="dim">All customers</span>
        ) : slugs.length === 0 ? (
          <span className="dim">None</span>
        ) : (
          <div className="grant-preview">
            <strong>{slugs.length}</strong>
            {slugs.slice(0, 3).map((s) => (
              <span key={s} className="chip tiny mono">{s}</span>
            ))}
            {slugs.length > 3 && <span className="dim">+{slugs.length - 3} more</span>}
          </div>
        )}
      </td>
      <td>
        <Time value={user.last_login_at} mode="day" fallback="never" />
      </td>
      <td style={{ textAlign: "right" }}>
        {editing ? (
          <span className="badge neu">Editing</span>
        ) : (
          <button type="button" className="tiny" onClick={onEdit}>Edit</button>
        )}
      </td>
    </tr>
  );
}

type GrantTab = "all" | "assigned" | "unassigned";

function UserPanel({
  user,
  users,
  customers,
  onClose,
  onSaved,
  onError,
}: {
  /** `null` creates a user. */
  user: UserSummary | null;
  users: UserSummary[];
  customers: CustomerDetail[];
  onClose: () => void;
  onSaved: (id: string) => void;
  onError: (m: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState(user?.name ?? "");
  const [role, setRole] = useState<Role>(user?.role ?? "csm");
  const [active, setActive] = useState(user?.is_active ?? true);
  // Grants are held as slugs, which is what the list endpoint reports; they
  // become ids only on the way out.
  const initial = useMemo(() => (user ? grantSlugs(user) : []), [user]);
  const [sel, setSel] = useState<string[]>(initial);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<GrantTab>("all");
  const [saving, setSaving] = useState(false);

  // Who else covers each customer, so assigning a second CSM is a decision
  // made knowingly. Inactive users are left out: they reach nothing.
  const others = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const u of users) {
      if (u.id === user?.id || !u.is_active) continue;
      for (const s of grantSlugs(u)) m.set(s, [...(m.get(s) ?? []), u.name]);
    }
    return m;
  }, [users, user]);

  const slugs = useMemo(() => customers.map((c) => c.name).sort(), [customers]);
  const byName = useMemo(() => new Map(customers.map((c) => [c.name, c])), [customers]);
  const isSel = (s: string) => sel.includes(s);
  const toggle = (s: string) => setSel(isSel(s) ? sel.filter((x) => x !== s) : [...sel, s]);

  const needle = q.trim().toLowerCase();
  const matches = slugs.filter(
    (s) =>
      !needle ||
      s.toLowerCase().includes(needle) ||
      (byName.get(s)?.display_name ?? "").toLowerCase().includes(needle),
  );
  const rows = matches.filter((s) =>
    tab === "assigned" ? isSel(s) : tab === "unassigned" ? !isSel(s) : true,
  );
  const selCount = slugs.filter(isSel).length;

  const added = sel.filter((s) => !initial.includes(s)).length;
  const removed = initial.filter((s) => !sel.includes(s)).length;
  const grantsChanged = role === "csm" && (added > 0 || removed > 0);
  const fieldsChanged = user
    ? name !== user.name || role !== user.role || active !== user.is_active
    : true;
  const diff = [
    added ? `+${added} added` : "",
    removed ? `−${removed} removed` : "",
    user && role !== user.role ? `role → ${role}` : "",
    user && active !== user.is_active ? (active ? "reactivated" : "deactivated") : "",
    user && name !== user.name ? "renamed" : "",
  ].filter(Boolean);

  const canSave = user ? fieldsChanged || grantsChanged : !!email.trim() && !!name.trim();

  const save = async () => {
    setSaving(true);
    try {
      let id = user?.id;
      if (user) {
        if (fieldsChanged) {
          await put(`/api/admin/users/${user.id}`, { name, role, is_active: active });
        }
      } else {
        ({ id } = await post<{ id: string }>("/api/admin/users", {
          email: email.trim(),
          name: name.trim(),
          role,
        }));
      }
      // An admin reaches every customer and stores no grant rows, so sending
      // a grant set for one is refused by the server -- invariant 8.
      if (role === "csm" && (grantsChanged || (!user && sel.length > 0))) {
        const ids = sel.map((s) => byName.get(s)?.id).filter((x): x is string => !!x);
        await put(`/api/admin/users/${id}/customers`, { customer_ids: ids });
      }
      onSaved(id!);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const title = user ? user.name : "New user";

  return (
    <aside className="side-panel" aria-label={user ? `Edit ${user.name}` : "Add a user"}>
      <div className="side-panel-head">
        <div className="side-panel-title">
          <div>
            <div className="side-panel-name">{title}</div>
            {user && <div className="dim mono">{user.email}</div>}
          </div>
          <button type="button" className="tiny" aria-label="Close panel" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        {!user && (
          <div>
            <label htmlFor="up-email">Email (Google Workspace)</label>
            <input
              id="up-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@arrowhead.team"
            />
          </div>
        )}
        <div className="side-panel-fields">
          <div className="grow">
            <label htmlFor="up-name">Name</label>
            <input id="up-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="up-role">Role</label>
            <select id="up-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="csm">csm</option>
              <option value="admin">admin</option>
            </select>
          </div>
          {user && (
            <label className="check">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
          )}
        </div>
      </div>

      <div className="side-panel-body">
        <div className="side-panel-section">
          <h2>Customers</h2>
          {role === "csm" && <span className="dim">{selCount} of {slugs.length}</span>}
        </div>

        {role === "admin" ? (
          <p className="dim">Admins reach every customer, so there is nothing to assign.</p>
        ) : (
          <>
            {selCount > 0 && (
              <div className="grant-chips">
                {slugs.filter(isSel).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="chip mono"
                    aria-label={`Remove ${s}`}
                    onClick={() => toggle(s)}
                  >
                    {s}
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor"
                      strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                      <path d="M2 2l6 6M8 2l-6 6" />
                    </svg>
                  </button>
                ))}
              </div>
            )}

            <label htmlFor="up-q" className="sr-only">Filter customers</label>
            <input
              id="up-q"
              type="search"
              placeholder="Filter customers…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <div className="tabs" role="tablist">
              {([
                ["all", "All", matches.length],
                ["assigned", "Assigned", matches.filter(isSel).length],
                ["unassigned", "Not assigned", matches.filter((s) => !isSel(s)).length],
              ] as const).map(([k, label, n]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={tab === k}
                  className={`tab ${tab === k ? "on" : ""}`}
                  onClick={() => setTab(k)}
                >
                  {label} <span className="count">{n}</span>
                </button>
              ))}
            </div>

            <div className="grant-list">
              {rows.map((s) => {
                const owners = others.get(s);
                return (
                  <label key={s} className="grant-row">
                    <input type="checkbox" checked={isSel(s)} onChange={() => toggle(s)} />
                    <span className="mono grow" title={byName.get(s)?.display_name}>{s}</span>
                    {owners && <span className="dim">{owners.join(", ")}</span>}
                  </label>
                );
              })}
              {rows.length === 0 && (
                <div className="empty">
                  {needle ? `No customers match “${q.trim()}”.` : "Nothing here."}
                </div>
              )}
            </div>

            <div className="grant-actions">
              <button
                type="button"
                className="tiny"
                onClick={() => setSel([...new Set([...sel, ...rows])])}
                disabled={rows.every(isSel)}
              >
                Select shown
              </button>
              <button type="button" className="tiny" onClick={() => setSel([])} disabled={sel.length === 0}>
                Clear all
              </button>
            </div>
          </>
        )}
      </div>

      <div className="side-panel-foot">
        <span className="dim grow">{user ? (diff.length ? diff.join(" · ") : "No changes") : ""}</span>
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="primary" onClick={save} disabled={!canSave || saving}>
          {user ? "Save changes" : "Create"}
        </button>
      </div>
    </aside>
  );
}

// --- Ingestion ----------------------------------------------------------

function Ingestion() {
  const channels = useResource<{ channels: ChannelDetail[] }>("/api/admin/channels");
  const mailboxes = useResource<{ mailboxes: MailboxDetail[] }>("/api/admin/mailboxes");
  const customers = useResource<{ customers: CustomerDetail[] }>("/api/admin/customers");

  const [ch, setCh] = useState({ slack_channel_id: "", name: "", customer_id: "", backfill_days: 14 });
  const [mb, setMb] = useState({ address: "", backfill_days: 30 });

  const addChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await put("/api/admin/channels", {
        slack_channel_id: ch.slack_channel_id.trim(),
        name: ch.name.trim() || null,
        customer_id: ch.customer_id || null,
        is_private: true,
        is_enabled: true,
        backfill_days: Number(ch.backfill_days),
      });
      setCh({ slack_channel_id: "", name: "", customer_id: "", backfill_days: 14 });
      channels.reload();
    } catch (err) {
      channels.setError((err as Error).message);
    }
  };

  const addMailbox = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await put("/api/admin/mailboxes", {
        address: mb.address.trim(),
        is_enabled: true,
        backfill_days: Number(mb.backfill_days),
      });
      setMb({ address: "", backfill_days: 30 });
      mailboxes.reload();
    } catch (err) {
      mailboxes.setError((err as Error).message);
    }
  };

  const toggleChannel = async (c: ChannelDetail) => {
    try {
      await put("/api/admin/channels", {
        slack_channel_id: c.slack_channel_id,
        customer_id: c.customer_id,
        name: c.name,
        is_private: c.is_private,
        is_enabled: !c.is_enabled,
        backfill_days: c.backfill_days,
      });
      channels.reload();
    } catch (err) {
      channels.setError((err as Error).message);
    }
  };

  return (
    <>
      <Err message={channels.error ?? mailboxes.error} />

      <h2>Slack channels</h2>
      <form onSubmit={addChannel}>
        <div className="row">
          <div>
            <label>Slack channel ID</label>
            <input
              value={ch.slack_channel_id}
              onChange={(e) => setCh({ ...ch, slack_channel_id: e.target.value })}
              placeholder="C0ADPPQET6H"
            />
          </div>
          <div>
            <label>Name</label>
            <input value={ch.name} onChange={(e) => setCh({ ...ch, name: e.target.value })} />
          </div>
          <div>
            <label>Customer</label>
            <select
              value={ch.customer_id}
              onChange={(e) => setCh({ ...ch, customer_id: e.target.value })}
            >
              <option value="">— unmapped —</option>
              {(customers.data?.customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ maxWidth: 120 }}>
            <label>Backfill days</label>
            <input
              type="number"
              value={ch.backfill_days}
              onChange={(e) => setCh({ ...ch, backfill_days: Number(e.target.value) })}
            />
          </div>
          <div className="tight">
            <button className="primary" disabled={!ch.slack_channel_id}>Save channel</button>
          </div>
        </div>
        <p className="sub">
          An unmapped channel is collected but invisible to every CSM until it is
          attached to a customer.
        </p>
      </form>

      {channels.data && (
        <table>
          <thead>
            <tr>
              <th>Channel</th><th>Name</th><th>Customer</th>
              <th>Backfill</th><th>Last polled</th><th>Enabled</th><th />
            </tr>
          </thead>
          <tbody>
            {channels.data.channels.map((c) => (
              <tr key={c.id} className={c.is_enabled ? "" : "off"}>
                <td className="mono">{c.slack_channel_id}</td>
                <td>{c.name ?? "—"}</td>
                <td>{c.customer_name ?? <em>unmapped</em>}</td>
                <td>{c.backfill_days}d</td>
                <td><Time value={c.last_polled_at} mode="relative" /></td>
                <td>{c.is_enabled ? "yes" : "no"}</td>
                <td>
                  <button onClick={() => toggleChannel(c)}>
                    {c.is_enabled ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Mailboxes</h2>
      <p className="sub">
        Mailboxes belong to no customer. A message is attributed by whose domain is on
        it, not by who received it, so these are global by design.
      </p>
      <form onSubmit={addMailbox} style={{ marginTop: 12 }}>
        <div className="row">
          <div>
            <label>Address</label>
            <input
              value={mb.address}
              onChange={(e) => setMb({ ...mb, address: e.target.value })}
              placeholder="someone@arrowhead.team"
            />
          </div>
          <div style={{ maxWidth: 120 }}>
            <label>Backfill days</label>
            <input
              type="number"
              value={mb.backfill_days}
              onChange={(e) => setMb({ ...mb, backfill_days: Number(e.target.value) })}
            />
          </div>
          <div className="tight">
            <button className="primary" disabled={!mb.address}>Save mailbox</button>
          </div>
        </div>
      </form>

      {mailboxes.data && (
        <table>
          <thead>
            <tr><th>Address</th><th>Backfill</th><th>Last polled</th><th>Enabled</th></tr>
          </thead>
          <tbody>
            {mailboxes.data.mailboxes.map((m) => (
              <tr key={m.id} className={m.is_enabled ? "" : "off"}>
                <td className="mono">{m.address}</td>
                <td>{m.backfill_days}d</td>
                <td><Time value={m.last_polled_at} mode="relative" /></td>
                <td>{m.is_enabled ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// --- Audit --------------------------------------------------------------

function Audit() {
  const { data, error } = useResource<{ entries: AuditRow[] }>("/api/admin/audit?limit=200");

  return (
    <>
      <Err message={error} />
      <h2>Recent activity</h2>
      <p className="sub">
        Every management change and sign-in, recorded in the same transaction as the
        change itself.
      </p>
      {!data ? (
        <div className="empty">Loading…</div>
      ) : (
        <table style={{ marginTop: 14 }}>
          <thead>
            <tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>Detail</th></tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.id}>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Time value={e.at} />
                </td>
                <td>{e.actor_email ?? "—"}</td>
                <td><code>{e.action}</code></td>
                <td>{e.target_type}</td>
                <td className="mono" style={{ color: "var(--muted)", maxWidth: 420, overflowWrap: "anywhere" }}>
                  {JSON.stringify(e.detail)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
